import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  ViewContainerRef,
} from '@angular/core';
import { AngularComponentFactory, QuestionAngular } from 'survey-angular-ui';
import { Serializer, Question } from 'survey-core';

export interface RangeSelectValue {
  min: number | null;
  max: number | null;
}

export class QuestionRangeSelectModel extends Question {
  public override getType(): string {
    return 'range-select';
  }

  public get unit(): string {
    return this.getPropertyValue('unit') || 'm²';
  }
  public set unit(val: string) {
    this.setPropertyValue('unit', val);
  }

  public get minPlaceholder(): string {
    return this.getPropertyValue('minPlaceholder') || 'Da';
  }
  public set minPlaceholder(val: string) {
    this.setPropertyValue('minPlaceholder', val);
  }

  public get maxPlaceholder(): string {
    return this.getPropertyValue('maxPlaceholder') || 'A';
  }
  public set maxPlaceholder(val: string) {
    this.setPropertyValue('maxPlaceholder', val);
  }

  public get buttonText(): string {
    return this.getPropertyValue('buttonText') || 'Conferma';
  }
  public set buttonText(val: string) {
    this.setPropertyValue('buttonText', val);
  }

  public get placeholder(): string {
    return this.getPropertyValue('placeholder') || 'Superficie';
  }
  public set placeholder(val: string) {
    this.setPropertyValue('placeholder', val);
  }

  public get minName(): string {
    return this.getPropertyValue('minName') || '';
  }
  public set minName(val: string) {
    this.setPropertyValue('minName', val);
  }

  public get maxName(): string {
    return this.getPropertyValue('maxName') || '';
  }
  public set maxName(val: string) {
    this.setPropertyValue('maxName', val);
  }

  public override isEmpty(): boolean {
    if (this.minName || this.maxName) {
      const surveyAny = this.survey as any;
      const minVal =
        this.minName && surveyAny ? surveyAny.getValue?.(this.minName) : null;
      const maxVal =
        this.maxName && surveyAny ? surveyAny.getValue?.(this.maxName) : null;
      return minVal == null && maxVal == null;
    }
    return super.isEmpty();
  }

  public get icon(): string {
    return this.getPropertyValue('icon') || 'size';
  }
  public set icon(val: string) {
    this.setPropertyValue('icon', val);
  }

  public get steps(): number[] {
    const rawChoices = (this as any)['choices'];
    if (Array.isArray(rawChoices) && rawChoices.length > 0) {
      return rawChoices
        .map((c: any) =>
          typeof c?.value === 'number' ? c.value : Number(c?.value)
        )
        .filter((n: number) => !isNaN(n));
    }
    const propSteps = this.getPropertyValue('steps');
    if (Array.isArray(propSteps) && propSteps.length > 0) {
      return propSteps;
    }
    return [
      20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 250, 300, 350, 400, 500,
      600, 700, 800, 900, 1000,
    ];
  }
  public set steps(val: number[]) {
    this.setPropertyValue('steps', val);
  }
}

export function registerRangeSelectQuestion(): void {
  if (!Serializer.findClass('range-select')) {
    Serializer.addClass(
      'range-select',
      [
        { name: 'unit', default: 'm²' },
        { name: 'minPlaceholder', default: 'Da' },
        { name: 'maxPlaceholder', default: 'A' },
        { name: 'buttonText', default: 'Conferma' },
        { name: 'placeholder', default: 'Superficie' },
        { name: 'minName', default: '' },
        { name: 'maxName', default: '' },
        { name: 'icon', default: 'size' },
        {
          name: 'steps:number[]',
          default: [
            20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 250, 300, 350, 400,
            500, 600, 700, 800, 900, 1000,
          ],
        },
      ],
      function () {
        return new QuestionRangeSelectModel('');
      },
      'question'
    );
  }
  AngularComponentFactory.Instance.registerComponent(
    'range-select-question',
    RangeSelectQuestionComponent
  );
}

@Component({
  selector: 'app-range-select-question',
  templateUrl: './range-select-question.component.html',
  styleUrls: ['./range-select-question.component.scss'],
})
export class RangeSelectQuestionComponent extends QuestionAngular<QuestionRangeSelectModel> {
  @ViewChild('minInput', { static: false })
  minInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('maxInput', { static: false })
  maxInputRef?: ElementRef<HTMLInputElement>;

  public isOpen: boolean = false;
  public activeField: 'min' | 'max' = 'min';

  public tempMin: number | null = null;
  public tempMax: number | null = null;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef<HTMLElement>
  ) {
    super(changeDetectorRef, viewContainerRef);
  }

  public get steps(): number[] {
    return this.model?.steps || [];
  }

  private getParsedValue(): RangeSelectValue | null {
    if (this.model?.survey && (this.model.minName || this.model.maxName)) {
      const surveyAny = this.model.survey as any;
      const minFromSurvey = this.model.minName
        ? surveyAny.getValue?.(this.model.minName)
        : null;
      const maxFromSurvey = this.model.maxName
        ? surveyAny.getValue?.(this.model.maxName)
        : null;
      if (minFromSurvey != null || maxFromSurvey != null) {
        return {
          min: minFromSurvey != null ? Number(minFromSurvey) : null,
          max: maxFromSurvey != null ? Number(maxFromSurvey) : null,
        };
      }
    }

    let val = this.model?.value;
    if (!val) return null;
    if (typeof val === 'string') {
      try {
        val = JSON.parse(val);
      } catch {
        return null;
      }
    }
    if (typeof val === 'object') {
      return {
        min: val.min != null ? Number(val.min) : null,
        max: val.max != null ? Number(val.max) : null,
      };
    }
    return null;
  }

  public get displayText(): string {
    const val = this.getParsedValue();
    const unit = this.model?.unit || 'm²';

    if (val?.min != null && val?.max != null) {
      return `${this.formatNumber(val.min)} ${unit} - ${this.formatNumber(val.max)} ${unit}`;
    } else if (val?.min != null) {
      return `Da ${this.formatNumber(val.min)} ${unit}`;
    } else if (val?.max != null) {
      return `Fino a ${this.formatNumber(val.max)} ${unit}`;
    }
    return this.model?.placeholder || 'Superficie';
  }

  public get hasSelectedValue(): boolean {
    const val = this.getParsedValue();
    return val?.min != null || val?.max != null;
  }

  public get minInputValue(): string {
    return this.tempMin != null ? String(this.tempMin) : '';
  }

  public get maxInputValue(): string {
    return this.tempMax != null ? String(this.tempMax) : '';
  }

  public get isIndifferentSelected(): boolean {
    if (this.activeField === 'min') {
      return this.tempMin == null;
    }
    return this.tempMax == null;
  }

  public isStepSelected(step: number): boolean {
    if (this.activeField === 'min') {
      return this.tempMin === step;
    }
    return this.tempMax === step;
  }

  public formatStep(step: number): string {
    return `${this.formatNumber(step)} ${this.model?.unit || 'm²'}`;
  }

  public formatNumber(num: number): string {
    return num.toLocaleString('it-IT');
  }

  public toggleDropdown(event: Event): void {
    if (this.model?.isReadOnly) return;
    event.stopPropagation();
    if (this.isOpen) {
      this.onConfirm();
    } else {
      this.openDropdown();
    }
    this.detectChanges();
  }

  public openDropdown(): void {
    const parsed = this.getParsedValue();
    this.tempMin = parsed?.min ?? null;
    this.tempMax = parsed?.max ?? null;
    this.activeField = 'min';
    this.isOpen = true;
    this.detectChanges();

    setTimeout(() => {
      this.minInputRef?.nativeElement?.focus();
    }, 50);
  }

  public setActiveField(field: 'min' | 'max'): void {
    this.activeField = field;
    this.detectChanges();
  }

  public onMinInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const digits = target.value.replace(/\D/g, '');
    this.tempMin = digits ? parseInt(digits, 10) : null;
    target.value = this.tempMin != null ? String(this.tempMin) : '';
    this.detectChanges();
  }

  public onMaxInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const digits = target.value.replace(/\D/g, '');
    this.tempMax = digits ? parseInt(digits, 10) : null;
    target.value = this.tempMax != null ? String(this.tempMax) : '';
    this.detectChanges();
  }

  public selectIndifferent(): void {
    if (this.activeField === 'min') {
      this.tempMin = null;
    } else {
      this.tempMax = null;
    }
    this.detectChanges();
  }

  public selectStep(step: number): void {
    if (this.activeField === 'min') {
      this.tempMin = step;
      this.activeField = 'max';
      this.detectChanges();
      setTimeout(() => {
        this.maxInputRef?.nativeElement?.focus();
      }, 0);
    } else {
      this.tempMax = step;
      this.detectChanges();
    }
  }

  public onConfirm(): void {
    let min = this.tempMin;
    let max = this.tempMax;

    if (min != null && max != null && min > max) {
      const tmp = min;
      min = max;
      max = tmp;
      this.tempMin = min;
      this.tempMax = max;
    }

    const hasSeparateFields = !!(this.model.minName || this.model.maxName);

    if (this.model.survey) {
      const surveyAny = this.model.survey as any;
      if (this.model.minName) {
        surveyAny.setValue?.(
          this.model.minName,
          min != null ? min : undefined
        );
      }
      if (this.model.maxName) {
        surveyAny.setValue?.(
          this.model.maxName,
          max != null ? max : undefined
        );
      }
    }

    if (hasSeparateFields) {
      // Per avere sempre un outcome plain ed evitare la duplicazione dell'oggetto,
      // non popoliamo model.value quando sono definiti minName/maxName
      this.model.value = undefined;
    } else {
      if (min == null && max == null) {
        this.model.value = undefined;
      } else {
        this.model.value = {
          min: min,
          max: max,
        };
      }
    }

    this.isOpen = false;
    this.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.onConfirm();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.detectChanges();
    }
  }
}

registerRangeSelectQuestion();
